import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { SizeOverride } from '@rover/components/src/Trustpilot';
import TrustPilotContainer from '@rover/components/src/Trustpilot/TrustPilotContainer';
import { Flex, Link, Paragraph } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';

type SectionProps = React.ComponentProps<typeof Section>;

export type RoverProtectProps = {
  countryConfig: Pick<
    CountryConfiguration,
    'hideBackgroundCheckVerbiage' | 'formattedVetCareReimbursementAmount'
  >;
  isTraining?: boolean;
  isGrooming?: boolean;
};

const RoverProtectCondensed: React.FC<RoverProtectProps & SectionProps> = ({
  countryConfig,
  isTraining = false,
  isGrooming = false,
  ...sectionProps
}) => {
  const { t } = useLingui();
  const vetCareReimbursementAmount =
    countryConfig?.formattedVetCareReimbursementAmount || '$25,000';

  let backgroundCheckStatement = t`Every sitter passes an enhanced background check.`;
  if (isTraining) {
    // DEV-104195: Translate
    backgroundCheckStatement =
      'Every trainer passes a background check and provides dog training credentials.';
  } else if (isGrooming) {
    // DEV-156190: Translate
    backgroundCheckStatement =
      'Every groomer passes a background check, provides grooming credentials, and passes a safety quiz.';
  }
  const idVerificationStatement = isGrooming
    ? // DEV-156190: Translate
      `Feel secure with ID-verified groomers and ${vetCareReimbursementAmount} in eligible vet care.`
    : t`Feel secure with ID-verified sitters and ${vetCareReimbursementAmount} in eligible vet care.`;
  return (
    <Section background="accent_secondary" py="5x" {...sectionProps}>
      <>
        <Paragraph webProps={{ maxWidth: '100%' }} textAlign="center" mb="1x">
          {countryConfig.hideBackgroundCheckVerbiage
            ? idVerificationStatement
            : backgroundCheckStatement}
          {!isTraining && !isGrooming && (
            <>
              {' '}
              <Link href="/rover-guarantee/">
                <Trans>Learn about the Rover Guarantee</Trans>
              </Link>
            </>
          )}
        </Paragraph>
        <Flex justifyContent="center">
          <TrustPilotContainer
            sizeOverride={SizeOverride.small}
            showBrandName={isGrooming}
            m="0x"
            pb="0x"
          />
        </Flex>
      </>
    </Section>
  );
};

export default RoverProtectCondensed;
